import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { Modal } from '../Modal';
import { useAlertSync } from '../../hooks/useAlertSync';
import { callCoinGeckoAPI } from '../../services/api';
import type { Token } from '../../types';
import type { CurrentPrices, PriceAlert } from '../../services/alertService';

interface AlertFormProps {
  isOpen: boolean;
  tokens: Token[];
  currentPrices: CurrentPrices;
  onClose: () => void;
  onSuccess: () => void;
}

type AlertScope = 'token' | 'portfolio';
type ConditionType = 'above' | 'below' | 'change_percent' | 'portfolio_change' | 'portfolio_ath' | 'token_ath';
type ChangeDirection = 'up' | 'down' | 'both';

export function AlertForm({ isOpen, tokens, currentPrices, onClose, onSuccess }: AlertFormProps) {
  const { t } = useTranslation();
  // Use the sync hook for creating alerts (syncs to Supabase)
  const { createAlert } = useAlertSync();

  const [alertScope, setAlertScope] = useState<AlertScope>('token');
  const [selectedTokenId, setSelectedTokenId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [condition, setCondition] = useState<ConditionType>('above');
  const [targetPrice, setTargetPrice] = useState('');
  const [changePercent, setChangePercent] = useState('');
  const [changeDirection, setChangeDirection] = useState<ChangeDirection>('both');
  const [repeat, setRepeat] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTokenList, setShowTokenList] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [localPrice, setLocalPrice] = useState<number | undefined>(undefined);
  const [hoveredCondition, setHoveredCondition] = useState<ConditionType | null>(null);
  const [hoveredTokenId, setHoveredTokenId] = useState<string | null>(null);
  const [hoveredScope, setHoveredScope] = useState<AlertScope | null>(null);
  const [hoveredDirection, setHoveredDirection] = useState<ChangeDirection | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isSelectingTokenRef = useRef(false);

  // Reset condition when scope changes
  useEffect(() => {
    if (alertScope === 'token') {
      setCondition('above');
    } else {
      setCondition('portfolio_change');
    }
  }, [alertScope]);

  // V2.I.10.c : Escape handling now provided by BaseModal (closeOnEscape
  // default = true). The previous V2.I.5.5 manual handler is removed.

  // Update dropdown position when showing
  useEffect(() => {
    if (!showTokenList || !searchInputRef.current) return;

    const updatePosition = () => {
      if (searchInputRef.current) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTokenList]);

  // Filter tokens based on search
  const filteredTokens = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const filtered = searchQuery
      ? tokens.filter(
          token =>
            token.name.toLowerCase().includes(query) ||
            token.symbol.toLowerCase().includes(query)
        )
      : tokens;
    return filtered.slice(0, 100);
  }, [tokens, searchQuery]);

  // Get selected token data
  const selectedToken = tokens.find(t => t.id === selectedTokenId);

  // Use local price if available, otherwise try from props
  const currentPrice = localPrice !== undefined ? localPrice : (selectedTokenId ? currentPrices[selectedTokenId]?.usd : undefined);

  // Fetch price for selected token
  useEffect(() => {
    if (!selectedTokenId) {
      setLocalPrice(undefined);
      return;
    }

    if (currentPrices[selectedTokenId]?.usd !== undefined) {
      setLocalPrice(currentPrices[selectedTokenId].usd);
      return;
    }

    setLocalPrice(undefined);
    callCoinGeckoAPI('/simple/price', {
      ids: selectedTokenId,
      vs_currencies: 'usd'
    })
      .then(data => {
        if (data[selectedTokenId]?.usd !== undefined) {
          setLocalPrice(data[selectedTokenId].usd);
        }
      })
      .catch(err => {
        console.error(`Error fetching price for ${selectedTokenId}:`, err);
        setLocalPrice(undefined);
      });
  }, [selectedTokenId, currentPrices]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation based on alert type
    if (alertScope === 'token' && condition !== 'token_ath') {
      if (!selectedTokenId) {
        setError(t('alerts.selectToken', 'Veuillez sélectionner un token'));
        return;
      }

      if (!selectedToken) {
        setError(t('alerts.invalidToken', 'Token invalide'));
        return;
      }

      if (condition === 'change_percent') {
        const changeValue = parseFloat(changePercent);
        if (isNaN(changeValue) || changeValue === 0) {
          setError(t('alerts.invalidPercent', 'Veuillez entrer un pourcentage valide (ex: +10 ou -5)'));
          return;
        }
      } else if (condition !== 'token_ath') {
        const priceValue = parseFloat(targetPrice);
        if (isNaN(priceValue) || priceValue <= 0) {
          setError(t('alerts.invalidPrice', 'Veuillez entrer un prix valide'));
          return;
        }

        if (currentPrice) {
          if (condition === 'above' && priceValue <= currentPrice) {
            setError(t('alerts.priceAboveError', 'Le prix cible doit être supérieur au prix actuel'));
            return;
          }
          if (condition === 'below' && priceValue >= currentPrice) {
            setError(t('alerts.priceBelowError', 'Le prix cible doit être inférieur au prix actuel'));
            return;
          }
        }
      }
    }

    // Token ATH requires a token selection
    if (condition === 'token_ath') {
      if (!selectedTokenId || !selectedToken) {
        setError(t('alerts.selectToken', 'Veuillez sélectionner un token'));
        return;
      }
    }

    // Portfolio change requires a percentage
    if (condition === 'portfolio_change') {
      const changeValue = parseFloat(changePercent);
      if (isNaN(changeValue) || changeValue <= 0) {
        setError(t('alerts.invalidPositivePercent', 'Veuillez entrer un pourcentage positif (ex: 10)'));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      let alertData: Omit<PriceAlert, 'id' | 'createdAt'>;

      switch (condition) {
        case 'above':
        case 'below':
          alertData = {
            tokenId: selectedTokenId,
            tokenName: selectedToken!.name,
            condition,
            targetPrice: parseFloat(targetPrice),
            repeatable: repeat,
            enabled: true,
          };
          break;

        case 'change_percent':
          alertData = {
            tokenId: selectedTokenId,
            tokenName: selectedToken!.name,
            condition: 'change_percent',
            changePercent: parseFloat(changePercent),
            basePrice: currentPrice || 0,
            repeatable: repeat,
            enabled: true,
          };
          break;

        case 'token_ath':
          alertData = {
            tokenId: selectedTokenId,
            tokenName: selectedToken!.name,
            condition: 'token_ath' as 'above' | 'below' | 'change_percent',
            targetPrice: currentPrice || 0, // Store current price as reference
            repeatable: repeat, // ATH alerts are typically repeatable
            enabled: true,
          };
          break;

        case 'portfolio_change':
          alertData = {
            tokenId: 'portfolio', // Special ID for portfolio alerts
            tokenName: t('alerts.portfolio', 'Portfolio'),
            condition: 'portfolio_change' as 'above' | 'below' | 'change_percent',
            changePercent: parseFloat(changePercent),
            repeatable: repeat,
            enabled: true,
            // Extra fields for portfolio alerts
            alertScope: 'portfolio',
            thresholdDirection: changeDirection,
          } as Omit<PriceAlert, 'id' | 'createdAt'>;
          break;

        case 'portfolio_ath':
          alertData = {
            tokenId: 'portfolio',
            tokenName: t('alerts.portfolio', 'Portfolio'),
            condition: 'portfolio_ath' as 'above' | 'below' | 'change_percent',
            repeatable: repeat, // ATH alerts are typically repeatable
            enabled: true,
            alertScope: 'portfolio',
          } as Omit<PriceAlert, 'id' | 'createdAt'>;
          break;

        default:
          throw new Error('Invalid condition type');
      }

      // Use hook's createAlert which syncs to Supabase
      await createAlert(alertData);

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alerts.createError', 'Erreur lors de la création'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // V2.I.10.c : `if (!isOpen) return null` removed — BaseModal handles
  // that internally (returns null when isOpen is false).

  // Token conditions
  const tokenConditions: { type: ConditionType; icon: React.ReactNode; label: string }[] = [
    { type: 'above', icon: <Icons.TrendingUp size={18} />, label: t('alerts.above', 'Au-dessus') },
    { type: 'below', icon: <Icons.TrendingDown size={18} />, label: t('alerts.below', 'En-dessous') },
    { type: 'change_percent', icon: <Icons.Activity size={18} />, label: t('alerts.changePercent', 'Variation %') },
    { type: 'token_ath', icon: <Icons.Award size={18} />, label: t('alerts.tokenATH', 'Nouvel ATH') },
  ];

  // Portfolio conditions
  const portfolioConditions: { type: ConditionType; icon: React.ReactNode; label: string }[] = [
    { type: 'portfolio_change', icon: <Icons.Activity size={18} />, label: t('alerts.dailyChange', 'Variation jour') },
    { type: 'portfolio_ath', icon: <Icons.Award size={18} />, label: t('alerts.portfolioATH', 'Nouvel ATH') },
  ];

  const activeConditions = alertScope === 'token' ? tokenConditions : portfolioConditions;

  // Check if we need token selection
  const needsTokenSelection = alertScope === 'token' || condition === 'token_ath';

  // Check if we need a price input
  const needsPriceInput = condition === 'above' || condition === 'below';

  // Check if we need percentage input
  const needsPercentInput = condition === 'change_percent' || condition === 'portfolio_change';

  // Check if we need direction selector (for portfolio change)
  const needsDirectionSelector = condition === 'portfolio_change';

  // Styles
  const styles = {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 10000,
    },
    modal: {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '90%',
      maxWidth: '420px',
      maxHeight: '90vh',
      zIndex: 10001,
      animation: 'modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      maxHeight: '90vh',
      background: 'var(--bg)',
      borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: '1px solid var(--border)',
      background: 'var(--card-bg)',
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '16px',
      fontWeight: 600,
      color: 'var(--text)',
      margin: 0,
    },
    closeBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      background: 'transparent',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      cursor: 'pointer',
      color: 'var(--muted)',
      transition: 'all 0.2s',
    },
    form: {
      flex: 1,
      padding: '20px',
      overflowY: 'auto' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '18px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: 'var(--text)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    tokenCount: {
      fontSize: '11px',
      fontWeight: 500,
      color: 'var(--muted)',
    },
    scopeSelector: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
    },
    scopeBtn: (isActive: boolean, isHovered: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px',
      background: isActive ? 'rgba(139, 92, 246, 0.15)' : 'var(--card-bg)',
      border: `2px solid ${isActive ? 'var(--primary)' : isHovered ? 'var(--primary)' : 'var(--border)'}`,
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: 600,
      color: isActive ? 'var(--primary)' : 'var(--text)',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    searchContainer: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '12px',
      color: 'var(--muted)',
      pointerEvents: 'none' as const,
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 38px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      fontSize: '14px',
      color: 'var(--text)',
      transition: 'all 0.2s',
      outline: 'none',
    },
    selectedToken: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px',
      background: 'rgba(139, 92, 246, 0.1)',
      border: '1px solid var(--primary)',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    conditionOptions: {
      display: 'grid',
      gridTemplateColumns: alertScope === 'token' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
      gap: '8px',
    },
    conditionBtn: (isActive: boolean, isHovered: boolean) => ({
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      padding: '12px 8px',
      background: isActive ? 'rgba(139, 92, 246, 0.1)' : 'var(--card-bg)',
      border: `2px solid ${isActive ? 'var(--primary)' : isHovered ? 'var(--primary)' : 'var(--border)'}`,
      borderRadius: '10px',
      fontSize: '10px',
      fontWeight: 600,
      color: isActive ? 'var(--primary)' : 'var(--text)',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    directionOptions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '8px',
    },
    directionBtn: (isActive: boolean, isHovered: boolean, color: string) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '10px',
      background: isActive ? `${color}15` : 'var(--card-bg)',
      border: `2px solid ${isActive ? color : isHovered ? color : 'var(--border)'}`,
      borderRadius: '10px',
      fontSize: '12px',
      fontWeight: 600,
      color: isActive ? color : 'var(--text)',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    input: {
      padding: '12px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      fontSize: '14px',
      color: 'var(--text)',
      transition: 'all 0.2s',
      outline: 'none',
    },
    inputHint: {
      fontSize: '11px',
      color: 'var(--muted)',
      fontStyle: 'italic' as const,
    },
    infoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      background: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.2)',
      borderRadius: '10px',
    },
    athInfoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      background: 'rgba(251, 191, 36, 0.1)',
      border: '1px solid rgba(251, 191, 36, 0.2)',
      borderRadius: '10px',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px',
      background: 'var(--card-bg)',
      borderRadius: '10px',
      border: '1px solid var(--border)',
      cursor: 'pointer',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      accentColor: 'var(--primary)',
      cursor: 'pointer',
    },
    errorMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 12px',
      background: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '12px',
      fontWeight: 500,
    },
    formActions: {
      display: 'flex',
      gap: '10px',
      paddingTop: '12px',
      borderTop: '1px solid var(--border)',
    },
    btnSecondary: {
      flex: 1,
      padding: '12px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      fontSize: '13px',
      fontWeight: 600,
      color: 'var(--text)',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    btnPrimary: {
      flex: 1,
      padding: '12px',
      background: 'var(--primary)',
      border: 'none',
      borderRadius: '10px',
      fontSize: '13px',
      fontWeight: 600,
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      opacity: isSubmitting ? 0.5 : 1,
    },
    tokenList: {
      position: 'fixed' as const,
      maxHeight: '280px',
      overflowY: 'auto' as const,
      border: '1px solid var(--border)',
      borderRadius: '10px',
      background: 'var(--card-bg)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
      zIndex: 100000,
      animation: 'dropdownSlide 0.2s ease-out',
    },
    tokenOption: (isHovered: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      padding: '10px 12px',
      background: isHovered ? 'var(--bg)' : 'transparent',
      border: 'none',
      borderBottom: '1px solid var(--border)',
      cursor: 'pointer',
      textAlign: 'left' as const,
      transition: 'all 0.15s',
    }),
    loadingMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px',
      background: 'rgba(249, 115, 22, 0.1)',
      border: '1px solid rgba(249, 115, 22, 0.3)',
      borderRadius: '10px',
      color: '#f97316',
      fontSize: '12px',
    },
    searchHint: {
      padding: '8px 12px',
      background: 'rgba(59, 130, 246, 0.1)',
      borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
      color: '#3b82f6',
      fontSize: '12px',
      textAlign: 'center' as const,
    },
    emptyState: {
      padding: '30px 20px',
      textAlign: 'center' as const,
      color: 'var(--muted)',
      fontSize: '13px',
    },
    dropdownCloseBtn: {
      position: 'absolute' as const,
      right: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      padding: 0,
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      cursor: 'pointer',
      color: 'var(--muted)',
    },
  };

  // Dropdown rendered with Portal to escape stacking context
  const dropdownPortal = showTokenList && !selectedToken && createPortal(
    <div
      style={{
        ...styles.tokenList,
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {filteredTokens.length === 0 ? (
        <div style={styles.emptyState}>{t('alerts.noTokenFound', 'Aucun token trouvé')}</div>
      ) : (
        <>
          {!searchQuery && tokens.length > 100 && (
            <div style={styles.searchHint}>
              {t('alerts.searchHint', '💡 Tapez pour rechercher parmi {{count}} cryptos', { count: tokens.length.toLocaleString() })}
            </div>
          )}
          {filteredTokens.map(token => (
            <button
              key={token.id}
              type="button"
              style={styles.tokenOption(hoveredTokenId === token.id)}
              onMouseEnter={() => setHoveredTokenId(token.id)}
              onMouseLeave={() => setHoveredTokenId(null)}
              onMouseDown={() => { isSelectingTokenRef.current = true; }}
              onTouchStart={() => { isSelectingTokenRef.current = true; }}
              onClick={() => {
                setSelectedTokenId(token.id);
                setSearchQuery('');
                setShowTokenList(false);
                isSelectingTokenRef.current = false;
              }}
            >
              {token.image && (
                <img
                  src={token.image}
                  alt={token.name}
                  style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {token.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase' }}>
                  {token.symbol || token.id}
                </div>
              </div>
              {currentPrices[token.id]?.usd !== undefined && (
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
                  ${currentPrices[token.id].usd.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </div>
              )}
            </button>
          ))}
        </>
      )}
    </div>,
    document.body
  );

  return (
    <>
      {/* V2.I.10.c : custom overlay/modal/container/header chrome
          replaced by <Modal> (BaseModal) — focus trap on Tab, body
          scroll lock, restore focus, portal render, role=dialog. */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`🔔 ${t('alerts.newAlert', 'Nouvelle alerte')}`}
        size="md"
      >
          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Alert Scope Selector */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t('alerts.alertType', "Type d'alerte")}
              </label>
              <div style={styles.scopeSelector}>
                <button
                  type="button"
                  style={styles.scopeBtn(alertScope === 'token', hoveredScope === 'token')}
                  onClick={() => setAlertScope('token')}
                  onMouseEnter={() => setHoveredScope('token')}
                  onMouseLeave={() => setHoveredScope(null)}
                >
                  <Icons.Coins size={18} />
                  {t('alerts.tokenAlert', 'Token')}
                </button>
                <button
                  type="button"
                  style={styles.scopeBtn(alertScope === 'portfolio', hoveredScope === 'portfolio')}
                  onClick={() => setAlertScope('portfolio')}
                  onMouseEnter={() => setHoveredScope('portfolio')}
                  onMouseLeave={() => setHoveredScope(null)}
                >
                  <Icons.Briefcase size={18} />
                  {t('alerts.portfolioAlert', 'Portfolio')}
                </button>
              </div>
            </div>

            {/* Condition Type */}
            <div style={styles.formGroup}>
              <label style={styles.label}>{t('alerts.condition', 'Condition')}</label>
              <div style={styles.conditionOptions}>
                {activeConditions.map(cond => (
                  <button
                    key={cond.type}
                    type="button"
                    style={styles.conditionBtn(condition === cond.type, hoveredCondition === cond.type)}
                    onClick={() => setCondition(cond.type)}
                    onMouseEnter={() => setHoveredCondition(cond.type)}
                    onMouseLeave={() => setHoveredCondition(null)}
                  >
                    {cond.icon}
                    <span>{cond.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Token Selection - only for token alerts or token_ath */}
            {needsTokenSelection && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Token
                  {tokens.length > 0 && (
                    <span style={styles.tokenCount}>({tokens.length} {t('alerts.available', 'disponibles')})</span>
                  )}
                </label>

                {tokens.length === 0 ? (
                  <div style={styles.loadingMessage}>
                    <Icons.AlertCircle size={16} />
                    <span>{t('alerts.loadingTokens', 'Chargement de la liste des cryptomonnaies...')}</span>
                  </div>
                ) : selectedToken ? (
                  <div
                    style={styles.selectedToken}
                    onClick={() => {
                      setSelectedTokenId('');
                      setShowTokenList(true);
                    }}
                  >
                    {selectedToken.image && (
                      <img
                        src={selectedToken.image}
                        alt={selectedToken.name}
                        style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                      />
                    )}
                    <span style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                      {selectedToken.name}
                    </span>
                    {currentPrice && (
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary)' }}>
                        ${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </span>
                    )}
                    <Icons.X size={16} style={{ color: 'var(--muted)' }} />
                  </div>
                ) : (
                  <div style={styles.searchContainer}>
                    <Icons.Search size={16} style={styles.searchIcon} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t('alerts.searchToken', 'Rechercher un token...')}
                      value={searchQuery}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        if (e.target.value) setShowTokenList(true);
                      }}
                      onFocus={() => {
                        if (searchQuery || tokens.length > 0) setShowTokenList(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          if (!isSelectingTokenRef.current) setShowTokenList(false);
                        }, 100);
                      }}
                      style={styles.searchInput}
                    />
                    {showTokenList && (
                      <button
                        type="button"
                        style={styles.dropdownCloseBtn}
                        onClick={() => {
                          setShowTokenList(false);
                          setSearchQuery('');
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Icons.X size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Target Price */}
            {needsPriceInput && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t('alerts.targetPrice', 'Prix cible ($)')}
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Ex: 50000"
                  value={targetPrice}
                  onChange={e => setTargetPrice(e.target.value)}
                  style={styles.input}
                  required
                />
                {currentPrice && (
                  <span style={styles.inputHint}>
                    {t('alerts.currentPrice', 'Prix actuel')}: ${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                  </span>
                )}
              </div>
            )}

            {/* Change Percent */}
            {needsPercentInput && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {condition === 'portfolio_change'
                    ? t('alerts.dailyChangePercent', 'Variation journalière (%)')
                    : t('alerts.changePercentLabel', 'Variation (%)')}
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder={condition === 'portfolio_change' ? 'Ex: 10' : 'Ex: +10 ou -5'}
                  value={changePercent}
                  onChange={e => setChangePercent(e.target.value)}
                  style={styles.input}
                  required
                />
                <span style={styles.inputHint}>
                  {condition === 'portfolio_change'
                    ? t('alerts.positivePercentHint', 'Entrez un pourcentage positif (ex: 10 pour ±10%)')
                    : t('alerts.percentHint', 'Entrez un nombre positif (+10) ou négatif (-5)')}
                </span>
              </div>
            )}

            {/* Direction Selector for Portfolio Change */}
            {needsDirectionSelector && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t('alerts.direction', 'Direction')}
                </label>
                <div style={styles.directionOptions}>
                  <button
                    type="button"
                    style={styles.directionBtn(changeDirection === 'up', hoveredDirection === 'up', '#22c55e')}
                    onClick={() => setChangeDirection('up')}
                    onMouseEnter={() => setHoveredDirection('up')}
                    onMouseLeave={() => setHoveredDirection(null)}
                  >
                    <Icons.TrendingUp size={16} />
                    {t('alerts.upOnly', 'Hausse')}
                  </button>
                  <button
                    type="button"
                    style={styles.directionBtn(changeDirection === 'down', hoveredDirection === 'down', '#ef4444')}
                    onClick={() => setChangeDirection('down')}
                    onMouseEnter={() => setHoveredDirection('down')}
                    onMouseLeave={() => setHoveredDirection(null)}
                  >
                    <Icons.TrendingDown size={16} />
                    {t('alerts.downOnly', 'Baisse')}
                  </button>
                  <button
                    type="button"
                    style={styles.directionBtn(changeDirection === 'both', hoveredDirection === 'both', '#8b5cf6')}
                    onClick={() => setChangeDirection('both')}
                    onMouseEnter={() => setHoveredDirection('both')}
                    onMouseLeave={() => setHoveredDirection(null)}
                  >
                    <Icons.Activity size={16} />
                    {t('alerts.both', 'Les deux')}
                  </button>
                </div>
              </div>
            )}

            {/* ATH Info Box */}
            {(condition === 'token_ath' || condition === 'portfolio_ath') && (
              <div style={styles.athInfoBox}>
                <Icons.Award size={20} style={{ color: '#fbbf24', flexShrink: 0 }} />
                <div style={{ fontSize: '12px', color: 'var(--text)' }}>
                  {condition === 'token_ath'
                    ? t('alerts.tokenATHInfo', 'Vous serez notifié chaque fois que ce token atteindra un nouveau plus haut historique depuis que vous avez créé cette alerte.')
                    : t('alerts.portfolioATHInfo', 'Vous serez notifié chaque fois que la valeur totale de votre portfolio atteindra un nouveau plus haut historique.')}
                </div>
              </div>
            )}

            {/* Portfolio Info Box */}
            {alertScope === 'portfolio' && condition === 'portfolio_change' && (
              <div style={styles.infoBox}>
                <Icons.Info size={20} style={{ color: '#3b82f6', flexShrink: 0 }} />
                <div style={{ fontSize: '12px', color: 'var(--text)' }}>
                  {t('alerts.portfolioChangeInfo', 'Cette alerte compare la valeur de votre portfolio par rapport à la veille. Idéal pour être averti des mouvements importants.')}
                </div>
              </div>
            )}

            {/* Repeat Option */}
            <div style={styles.formGroup}>
              <div
                style={styles.checkboxContainer}
                onClick={() => setRepeat(!repeat)}
              >
                <input
                  type="checkbox"
                  checked={repeat}
                  onChange={e => setRepeat(e.target.checked)}
                  style={styles.checkbox}
                  onClick={e => e.stopPropagation()}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>
                    <Icons.RefreshCw size={14} style={{ marginRight: '6px', color: repeat ? '#3b82f6' : 'var(--muted)' }} />
                    {t('alerts.repeatable', 'Alerte répétable')}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                    {t('alerts.repeatableHint', 'Se réactive après 1h de cooldown')}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={styles.errorMessage}>
                <Icons.AlertCircle size={14} />
                {error}
              </div>
            )}

            {/* Form Actions */}
            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="submit"
                style={styles.btnPrimary}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('alerts.creating', 'Création...') : t('alerts.create', "Créer l'alerte")}
              </button>
            </div>
          </form>
      </Modal>

      {/* Dropdown rendered via Portal */}
      {dropdownPortal}

      {/* CSS animations */}
      <style>{`
        @keyframes modalSlideUp {
          from {
            transform: translate(-50%, -40%);
            opacity: 0;
          }
          to {
            transform: translate(-50%, -50%);
            opacity: 1;
          }
        }
        @keyframes dropdownSlide {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
